import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Badge } from "@/ui/components/primitives/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/primitives/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";
import { Switch } from "@/ui/components/primitives/switch";
import { Checkbox } from "@/ui/components/primitives/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/components/primitives/table";
import {
  Plus,
  RefreshCw,
  Search,
  Settings,
  Server,
  CheckCircle,
  XCircle,
  Loader2,
  Edit,
  Trash2,
  Eye,
} from "lucide-react";
import {
  fetchPSPConfigurations,
  fetchPSPConfigStats,
  fetchPSPHealth,
  togglePSPConfigStatus,
  bulkTogglePSPConfigs,
  deletePSPConfiguration,
  type PSPConfiguration,
  type PSPConfigStats,
  type PSPHealth,
} from "@/services/api/services/psp-management";
import { showErrorToast, showSuccessToast } from "@/services/toast-helper";
import { PSPStatusBadge, ProviderBadge } from "../components/psp-status-badge";
import { PSPHealthIndicator } from "../components/psp-health-indicator";
import { CreatePSPConfigModal } from "../components/create-psp-config-modal";
import { EditPSPConfigModal } from "../components/edit-psp-config-modal";
import { cn } from "@/ui/components/primitives/utils";

export default function PSPManagementPage() {
  // URL-based state for filters
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "ALL";
  const providerFilter = searchParams.get("provider") || "ALL";
  const sandboxFilter = searchParams.get("sandbox") || "ALL";

  // Local state
  const [configs, setConfigs] = useState<PSPConfiguration[]>([]);
  const [stats, setStats] = useState<PSPConfigStats | null>(null);
  const [healthData, setHealthData] = useState<PSPHealth[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Search input with debounce
  const [searchInput, setSearchInput] = useState(search);

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<PSPConfiguration | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Available providers for filter
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);

  // Update URL params
  const updateParams = useCallback(
    (updates: Record<string, string | number | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === "" || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        updateParams({ search: searchInput || null, page: 1 });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, updateParams]);

  // Load PSP configurations
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const filters: any = { page, limit };
      if (search) filters.merchantId = search;
      if (statusFilter !== "ALL")
        filters.isEnabled = statusFilter === "ENABLED";
      if (providerFilter !== "ALL") filters.provider = providerFilter;
      if (sandboxFilter !== "ALL")
        filters.isSandbox = sandboxFilter === "SANDBOX";

      const res = await fetchPSPConfigurations(filters);
      // Ensure data is an array before setting
      const configData = Array.isArray(res.data) ? res.data : [];
      setConfigs(configData);
      setTotalPages(res.pagination?.totalPages || 0);
      setTotalItems(res.pagination?.total || 0);

      // Extract unique providers for filter
      const providers = Array.from(
        new Set(configData.map((c) => c.provider))
      ).sort();
      setAvailableProviders(providers);
    } catch (e: any) {
      setError(
        e?.detail?.error?.message || e?.message || "Failed to load PSP configurations"
      );
      showErrorToast("Failed to load PSP configurations");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, providerFilter, sandboxFilter]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const statsData = await fetchPSPConfigStats();
      setStats(statsData);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  }, []);

  // Load health data
  const loadHealth = useCallback(async () => {
    try {
      const health = await fetchPSPHealth();
      // Ensure health is always an array
      setHealthData(Array.isArray(health) ? health : []);
    } catch (error) {
      console.error("Failed to load health data:", error);
      setHealthData([]); // Set empty array on error
    }
  }, []);

  // Load all data
  const loadAllData = useCallback(() => {
    loadConfigs();
    loadStats();
    loadHealth();
  }, [loadConfigs, loadStats, loadHealth]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Toggle PSP status
  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await togglePSPConfigStatus(id, !currentStatus);
      showSuccessToast(`PSP ${!currentStatus ? "enabled" : "disabled"} successfully`);
      loadAllData();
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to update PSP status");
    }
  };

  // Delete PSP configuration
  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this PSP configuration?")) {
      return;
    }

    try {
      await deletePSPConfiguration(id);
      showSuccessToast("PSP configuration deleted successfully");
      loadAllData();
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to delete PSP configuration");
    }
  };

  // Edit PSP configuration
  const handleEdit = (config: PSPConfiguration) => {
    setSelectedConfig(config);
    setEditModalOpen(true);
  };

  // Bulk selection toggle
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(configs.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((sid) => sid !== id));
    }
  };

  // Bulk actions
  const handleBulkEnable = async () => {
    if (selectedIds.length === 0) {
      showErrorToast("No configurations selected");
      return;
    }

    setBulkActionLoading(true);
    try {
      await bulkTogglePSPConfigs(selectedIds, true);
      showSuccessToast(`${selectedIds.length} configurations enabled successfully`);
      setSelectedIds([]);
      loadAllData();
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to enable configurations");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDisable = async () => {
    if (selectedIds.length === 0) {
      showErrorToast("No configurations selected");
      return;
    }

    setBulkActionLoading(true);
    try {
      await bulkTogglePSPConfigs(selectedIds, false);
      showSuccessToast(`${selectedIds.length} configurations disabled successfully`);
      setSelectedIds([]);
      loadAllData();
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to disable configurations");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Get health status for a provider
  const getHealthForProvider = (provider: string) => {
    // Ensure healthData is an array before calling find
    if (!Array.isArray(healthData)) {
      return { status: "UNKNOWN" as const, responseTime: 0, successRate: 0, lastChecked: "" };
    }
    const health = healthData.find((h) => h.provider === provider);
    return health || { status: "UNKNOWN" as const, responseTime: 0, successRate: 0, lastChecked: "" };
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchInput("");
    updateParams({
      search: null,
      status: null,
      provider: null,
      sandbox: null,
      page: 1,
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">PSP Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage Payment Service Providers and their configurations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadAllData()}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Server className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </Card>
          <Card className="p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Enabled</p>
                <p className="text-2xl font-bold text-green-600">{stats.enabled}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-20" />
            </div>
          </Card>
          <Card className="p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Disabled</p>
                <p className="text-2xl font-bold text-gray-600">{stats.disabled}</p>
              </div>
              <XCircle className="h-8 w-8 text-gray-500 opacity-20" />
            </div>
          </Card>
          <Card className="p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Sandbox</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.sandbox}</p>
              </div>
              <Settings className="h-8 w-8 text-yellow-500 opacity-20" />
            </div>
          </Card>
          <Card className="p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Production</p>
                <p className="text-2xl font-bold text-purple-600">{stats.production}</p>
              </div>
              <Server className="h-8 w-8 text-purple-500 opacity-20" />
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="configurations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="configurations">
            <Settings className="h-4 w-4 mr-2" />
            Merchant Configurations
          </TabsTrigger>
          <TabsTrigger value="providers">
            <Server className="h-4 w-4 mr-2" />
            PSP Providers
          </TabsTrigger>
        </TabsList>

        {/* Merchant Configurations Tab */}
        <TabsContent value="configurations" className="space-y-4">
          {/* Filter Bar */}
          <Card className="p-4">
            <div className="flex flex-col gap-4">
              {/* First Row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by merchant ID..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select
                  value={statusFilter}
                  onValueChange={(v) => updateParams({ status: v === "ALL" ? null : v, page: 1 })}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Status</SelectItem>
                    <SelectItem value="ENABLED">Enabled</SelectItem>
                    <SelectItem value="DISABLED">Disabled</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={providerFilter}
                  onValueChange={(v) => updateParams({ provider: v === "ALL" ? null : v, page: 1 })}
                >
                  <SelectTrigger className="w-full sm:w-44">
                    <SelectValue placeholder="Provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Providers</SelectItem>
                    {availableProviders.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={sandboxFilter}
                  onValueChange={(v) => updateParams({ sandbox: v === "ALL" ? null : v, page: 1 })}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Modes</SelectItem>
                    <SelectItem value="SANDBOX">Sandbox</SelectItem>
                    <SelectItem value="PRODUCTION">Production</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={handleResetFilters}>
                  Reset
                </Button>
              </div>

              {/* Bulk Actions Bar */}
              {selectedIds.length > 0 && (
                <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">
                    {selectedIds.length} selected
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkEnable}
                    disabled={bulkActionLoading}
                  >
                    {bulkActionLoading && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                    Enable
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkDisable}
                    disabled={bulkActionLoading}
                  >
                    {bulkActionLoading && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                    Disable
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedIds([])}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Desktop Table */}
          <Card className="hidden md:block">
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          configs.length > 0 &&
                          selectedIds.length === configs.length
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Merchant ID</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Methods</TableHead>
                    <TableHead>Health</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-muted-foreground">Loading configurations...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <p className="text-destructive">{error}</p>
                      </TableCell>
                    </TableRow>
                  ) : configs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <p className="text-muted-foreground">No PSP configurations found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    configs.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(config.id)}
                            onCheckedChange={(checked) =>
                              handleSelectOne(config.id, checked as boolean)
                            }
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {config.merchantId.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <ProviderBadge provider={config.provider} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={config.isEnabled}
                              onCheckedChange={() =>
                                handleToggleStatus(config.id, config.isEnabled)
                              }
                            />
                            <PSPStatusBadge isEnabled={config.isEnabled} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <PSPStatusBadge
                            isEnabled={config.isEnabled}
                            isSandbox={config.isSandbox}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{config.priority}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {config.paymentMethods.slice(0, 2).map((method) => (
                              <Badge key={method} variant="secondary" className="text-xs">
                                {method}
                              </Badge>
                            ))}
                            {config.paymentMethods.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{config.paymentMethods.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <PSPHealthIndicator
                            {...getHealthForProvider(config.provider)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(config)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(config.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {!loading && configs.length > 0 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {configs.length} of {totalItems} items (Page {page} of {totalPages})
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => updateParams({ page: page - 1 })}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">Page {page}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => updateParams({ page: page + 1 })}
                  >
                    Next
                  </Button>
                  <Select
                    value={String(limit)}
                    onValueChange={(v) => updateParams({ limit: v, page: 1 })}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </Card>

          {/* Mobile Card View */}
          <Card className="md:hidden">
            <div className="space-y-3 p-3">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <p className="text-muted-foreground mt-2">Loading...</p>
                </div>
              ) : configs.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No configurations found</p>
                </div>
              ) : (
                configs.map((config) => (
                  <div key={config.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <ProviderBadge provider={config.provider} />
                        <p className="text-xs text-muted-foreground mt-1 font-mono">
                          {config.merchantId.slice(0, 12)}...
                        </p>
                      </div>
                      <Switch
                        checked={config.isEnabled}
                        onCheckedChange={() =>
                          handleToggleStatus(config.id, config.isEnabled)
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <PSPStatusBadge isEnabled={config.isEnabled} />
                      <PSPStatusBadge
                        isEnabled={config.isEnabled}
                        isSandbox={config.isSandbox}
                      />
                      <Badge variant="outline">P: {config.priority}</Badge>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <PSPHealthIndicator {...getHealthForProvider(config.provider)} />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(config)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(config.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Mobile Pagination */}
            {!loading && configs.length > 0 && (
              <div className="p-4 border-t space-y-3">
                <p className="text-sm text-muted-foreground text-center">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={page <= 1}
                    onClick={() => updateParams({ page: page - 1 })}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={page >= totalPages}
                    onClick={() => updateParams({ page: page + 1 })}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* PSP Providers Tab */}
        <TabsContent value="providers">
          <Card className="p-8">
            <div className="text-center space-y-4">
              <Server className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
              <div>
                <h3 className="text-lg font-semibold">PSP Providers Management</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  PSP provider management will be available here. You can manage WPAY, LAFFDA,
                  funpay, cryptopsp and other payment service providers.
                </p>
              </div>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Provider (Coming Soon)
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CreatePSPConfigModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreateComplete={loadAllData}
      />
      <EditPSPConfigModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        config={selectedConfig}
        onUpdateComplete={loadAllData}
      />
    </div>
  );
}
